import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import type { PageOrientation } from '@record-fill';
import { sheetWidthFor } from '../lib/sheet-pages';
import {
  useCanvasResponsiveWidth,
  type CanvasResponsiveWidthOptions,
} from './useCanvasResponsiveWidth';

/**
 * Lays the sheet out at the builder width and fits it to the screen with a scale only. Element
 * widths are percentages and fonts are fixed px, so laying it out at any other width would wrap
 * lines and break pages differently from the builder and the exported PDF.
 *
 * The scroller around the measured container needs `[scrollbar-gutter:stable]`: the scale sets the
 * sheet height, so a classic scrollbar that appears and disappears with that height narrows the
 * container, lowers the scale and removes itself again, and the sheet never settles.
 */
export function useSheetFit(
  containerRef: Ref<HTMLElement | null>,
  orientation: MaybeRefOrGetter<PageOrientation>,
  options: CanvasResponsiveWidthOptions = {}
) {
  const { displayWidth: availableWidth } = useCanvasResponsiveWidth(containerRef, options);

  const sheetWidth = computed(() => sheetWidthFor(toValue(orientation)));
  const fitScale = computed(() => availableWidth.value / sheetWidth.value);

  return { sheetWidth, fitScale };
}
